<template>
    <form :id="formId" class="flex flex-col gap-4" @submit.prevent="handleSubmit">
        <fieldset :disabled="!!savedNotice || isSaving" class="contents">
            <div class="grid gap-4 sm:grid-cols-2">
                <ConnectionBasicFields
                    ref="basicFields"
                    v-model:name="name"
                    v-model:job-title="jobTitle"
                    v-model:email="email"
                    :show-email="publicProfileEnabled"
                    :errors="fieldErrors" />
                <fieldset>
                    <legend class="body-small text-control-label mb-2">{{ t("connections.edit.fields.network.legend") }}</legend>
                    <div class="flex min-h-10 flex-wrap items-center gap-x-4 gap-y-2">
                        <BaseCheckbox v-model="inTeam">
                            <span class="body-small">{{ t("connections.edit.fields.network.inTeam") }}</span>
                        </BaseCheckbox>
                        <BaseCheckbox v-model="inOrg">
                            <span class="body-small">{{ t("connections.edit.fields.network.inOrg") }}</span>
                        </BaseCheckbox>
                    </div>
                </fieldset>
            </div>
            <BaseCheckbox v-if="publicProfileEnabled" v-model="linkProfile" shape="square" :disabled="!hasEmail">
                <span class="body-small" :class="!hasEmail && 'text-control-label-disabled'">{{
                    hasEmail ? t("connections.addPeopleDialog.manual.linkProfile") : t("connections.addPeopleDialog.manual.linkProfileHint")
                }}</span>
            </BaseCheckbox>
            <div class="flex flex-col gap-2">
                <p class="body-small text-control-label">{{ t("connections.edit.notes.heading") }}</p>
                <VoiceTextarea
                    v-model:input="notes"
                    v-model:busy="isDictating"
                    rows="2"
                    :textarea-aria-label="t('connections.edit.notes.heading')"
                    :placeholder="t('connections.edit.notes.placeholder')" />
            </div>
        </fieldset>
        <BaseNotification v-if="errorMessage" theme="error" hide-dismiss :text="errorMessage" />
        <BaseNotification v-else-if="savedNotice" :theme="savedNotice === 'linkFailed' ? 'warning' : 'info'" hide-dismiss :text="savedNoticeText" />
    </form>
    <Teleport defer :to="footerTarget">
        <template v-if="savedNotice">
            <BaseButton v-if="savedNotice === 'linkFailed'" theme="tertiary" :disabled="isLinking" @click="link">{{ t("common.tryAgain") }}</BaseButton>
            <BaseButton theme="primary" :disabled="isLinking" @click="emit('added')">{{ t("common.done") }}</BaseButton>
        </template>
        <template v-else>
            <BaseButton theme="tertiary" :disabled="isSaving" @click="emit('cancel')">{{ t("common.cancel") }}</BaseButton>
            <BaseButton type="submit" :form="formId" theme="primary" :disabled="!name || !jobTitle || isSaving || isDictating">
                {{ t("connections.addPeopleDialog.manual.submit") }}
            </BaseButton>
        </template>
    </Teleport>
</template>

<script setup lang="ts">
import { extractApiFieldErrors, useFetch } from "~vue/api";
import type { DirectoryLinkByEmailRequest, DirectoryLinkByEmailResult, ProfileDirectorySource } from "~vue/api/generated/collab-profiles";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import VoiceTextarea from "~vue/components/form/VoiceTextarea.vue";
import ConnectionBasicFields from "~vue/Connections/ConnectionBasicFields.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { computed, nextTick, ref, useId, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const { source, footerTarget, publicProfileEnabled } = defineProps<{ source: ProfileDirectorySource; footerTarget: string; publicProfileEnabled?: boolean }>();
const emit = defineEmits<{ saved: []; added: []; cancel: []; busy: [value: boolean] }>();

const { t } = useI18n();
const formId = useId();
const basicFields = useTemplateRef<InstanceType<typeof ConnectionBasicFields>>("basicFields");

const name = ref("");
const jobTitle = ref("");
const email = ref("");
const inTeam = ref(false);
const inOrg = ref(false);
const notes = ref("");
const hasEmail = computed(() => !!email.value.trim());
// Checked by default once there's an email, but an explicit choice survives clearing and re-typing it.
const linkChoice = ref<boolean | null>(null);
const linkProfile = computed({
    get: () => hasEmail.value && (linkChoice.value ?? true),
    set: (value: boolean) => (linkChoice.value = value),
});

// Same shape and comfort default as a new person saved from ConnectionEdit.
const payload = computed(() => ({
    job_title: jobTitle.value,
    in_team: inTeam.value,
    in_org: inOrg.value,
    persona: { name: name.value, relationship: jobTitle.value },
    influence: 2,
    note: { raw_note: notes.value },
    ...(hasEmail.value && { alias: { value: email.value } }),
}));
const {
    data: created,
    error: createError,
    execute: executeCreate,
    isFetching: isCreating,
} = useFetch("/api/connections/", { immediate: false }).post(payload).json<{ id: string }>();

const linkPayload = computed<DirectoryLinkByEmailRequest>(() => ({ email: email.value, source, connection_id: created.value?.id ?? "" }));
const {
    data: linkResult,
    error: linkError,
    execute: executeLink,
    isFetching: isLinking,
} = useFetch("/v1/collaboration-profiles/directory/link-by-email", { immediate: false }).post(linkPayload).json<DirectoryLinkByEmailResult>();

// Set once the person is saved but the dialog stays open to report on the profile link.
const savedNotice = ref<"alreadyLinked" | "linkFailed" | null>(null);
const savedNoticeText = computed(() =>
    savedNotice.value === "linkFailed"
        ? t("connections.addPeopleDialog.manual.linkFailed", { name: name.value })
        : t("connections.addPeopleDialog.manual.alreadyLinked", { name: name.value }),
);
const isSaving = computed(() => isCreating.value || isLinking.value);
const isDictating = ref(false);
// A saved person's notice (already linked, or link failed with Try again) stays until Done, like the CSV result screen.
watch(
    () => isSaving.value || isDictating.value || !!savedNotice.value,
    (value) => emit("busy", value),
);
// As in ConnectionEdit: field errors render on their field, and the banner stays only for ones no field can show.
const fieldErrors = ref<Record<string, string>>({});
const errorMessage = computed(() => {
    if (isSessionEndedError(createError.value)) {
        return null;
    }
    const fields = Object.keys(fieldErrors.value);
    if (fields.length > 0 && fields.every((field) => basicFields.value?.showsError(field))) {
        return null;
    }
    return createError.value?.message;
});

async function handleSubmit() {
    if (!name.value || !jobTitle.value || isSaving.value || isDictating.value || savedNotice.value) {
        return;
    }
    fieldErrors.value = {};
    await executeCreate();
    if (createError.value) {
        fieldErrors.value = extractApiFieldErrors(created.value);
        nextTick(() => basicFields.value?.focusFirstError(Object.keys(fieldErrors.value)));
        return;
    }
    logUserInteraction(AmplitudeEventNames.CONNECTION_ADD, { source: "connection_edit", added_context: notes.value.length > 0 });
    emit("saved");
    if (linkProfile.value && hasEmail.value) {
        await link();
    } else {
        emit("added");
    }
}

async function link() {
    await executeLink();
    if (linkError.value) {
        logError("Linking a manually added person's Collaboration Profile failed", linkError.value);
        savedNotice.value = "linkFailed";
        return;
    }
    const linkedConnectionId = linkResult.value?.connection_id;
    if (linkedConnectionId && linkedConnectionId !== created.value?.id) {
        savedNotice.value = "alreadyLinked";
        return;
    }
    emit("added");
}
</script>
